import React, { useEffect, useState } from 'react';
import { replace } from 'connected-react-router';
import { ArrowLeft } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { getLanguage } from '@/services/GNRL';
import { translate } from '@/services/TranslationService';
import { toast } from '@/components/ui/use-toast';
import { getDerivedAuthority } from '../authoritySelection/utilities';
import { withNOVOHeader } from '../sideMenu/HeaderHOC';
import DiscussionListItem from './DiscussionListItem';
import DiscussionsEmptyList from './DiscussionsEmptyList';
import DiscussionsLoading from './DiscussionsLoading';
import { getDiscussions } from './actions';

const Discussions = (props) => {
  const { setHeaderTitle } = props;

  const [activeDiscussion, setActiveDiscussion] = useState(undefined);
  const [hasHandledQueryDiscussion, setHasHandledQueryDiscussion] =
    useState(false);
  const [hasObservedLoadingCycle, setHasObservedLoadingCycle] = useState(false);
  const hasSetHeaderTitleRef = React.useRef(false);
  const lastFetchedAuthorityRef = React.useRef(null);

  const dispatch = useDispatch();

  const {
    loading,
    error,
    discussions,
    routeSearch,
    routePathname,
    authToken,
    derivedAuthority,
  } = useSelector((state) => ({
    ...(state.discussionsReducer || {}),
    routeSearch: state.router?.location?.search || '',
    routePathname: state.router?.location?.pathname || '/discussions',
    authToken: state.authReducer?.auth_token,
    derivedAuthority:
      state.profileReducer.profile.authority?.name ||
      getDerivedAuthority(state),
  }));

  const createWebChatUrl = (discussionName) => {
    const baseUrl = process.env.REACT_APP_BASE_URL_WEBCHAT;
    const target = 'consultations';
    const language = getLanguage() === 'gr' ? 'el' : getLanguage();

    return `${baseUrl}?name=${discussionName}___${derivedAuthority}&Token=${authToken}&target=${target}&medium=app&language=${language}`;
  };

  const queryDiscussionName = React.useMemo(() => {
    const params = new URLSearchParams(routeSearch);
    return (params.get('name') || '').trim();
  }, [routeSearch]);

  useEffect(() => {
    if (hasSetHeaderTitleRef.current) return;
    setHeaderTitle(translate('sideMenu.Consultations'));
    hasSetHeaderTitleRef.current = true;
  }, [setHeaderTitle]);

  useEffect(() => {
    if (!derivedAuthority) return;
    if (lastFetchedAuthorityRef.current === derivedAuthority) return;

    lastFetchedAuthorityRef.current = derivedAuthority;
    setHasObservedLoadingCycle(false);
    setHasHandledQueryDiscussion(false);
    dispatch(getDiscussions());
  }, [derivedAuthority, dispatch]);

  useEffect(() => {
    if (loading) {
      setHasObservedLoadingCycle(true);
    }
  }, [loading]);

  useEffect(() => {
    setHasHandledQueryDiscussion(false);
  }, [queryDiscussionName]);

  useEffect(() => {
    if (error) {
      // TODO: Add translation
      // TODO: Add error handling
      toast({
        title: 'Error',
        description: translate('Failed to fetch discussions'),
        variant: 'destructive',
      });
    }
  }, [error]);

  useEffect(() => {
    if (!queryDiscussionName) return;
    if (loading || error) return;
    if (!hasObservedLoadingCycle) return;
    if (hasHandledQueryDiscussion) return;

    setActiveDiscussion(queryDiscussionName);
    setHasHandledQueryDiscussion(true);
  }, [
    error,
    hasHandledQueryDiscussion,
    hasObservedLoadingCycle,
    loading,
    queryDiscussionName,
  ]);

  const syncDiscussionQuery = React.useCallback(
    (discussionName) => {
      const params = new URLSearchParams(routeSearch);
      const currentName = (params.get('name') || '').trim();

      if (discussionName) {
        if (currentName === discussionName) return;
        params.set('name', discussionName);
      } else {
        if (!currentName) return;
        params.delete('name');
      }

      const nextSearch = params.toString();

      dispatch(
        replace({
          pathname: routePathname,
          search: nextSearch ? `?${nextSearch}` : '',
        })
      );
    },
    [dispatch, routePathname, routeSearch]
  );

  const handleOpenDiscussion = React.useCallback(
    (discussionName) => {
      setActiveDiscussion(discussionName);
      syncDiscussionQuery(discussionName);
    },
    [syncDiscussionQuery]
  );

  const handleCloseDiscussion = React.useCallback(() => {
    setActiveDiscussion(undefined);
    syncDiscussionQuery(undefined);
  }, [syncDiscussionQuery]);

  useEffect(() => {
    if (!hasObservedLoadingCycle) return;

    const params = new URLSearchParams(routeSearch);
    const currentName = (params.get('name') || '').trim();
    if (!currentName && activeDiscussion) {
      setActiveDiscussion(undefined);
    }
  }, [activeDiscussion, hasObservedLoadingCycle, routeSearch]);

  return (
    <div className="flex flex-1 flex-col relative">
      {loading && <DiscussionsLoading />}
      {discussions.length === 0 && !loading && <DiscussionsEmptyList />}

      {!activeDiscussion && discussions.length > 0 && (
        <div className="flex flex-col sm:m-10 m-0 sm:max-h-[calc(100vh-10rem)] py-2 px-8 overflow-y-auto bg-white">
          {discussions.map((discussion) => (
            <div
              key={discussion.name}
              className="flex border-b-2 last:border-b-0 border-gray-100"
            >
              <DiscussionListItem
                discussion={discussion}
                setActiveDiscussion={handleOpenDiscussion}
              />
            </div>
          ))}
        </div>
      )}
      {activeDiscussion && (
        <div className="flex flex-col flex-1 py-4 px-6 bg-white ">
          <div className="flex">
            <ArrowLeft
              size={24}
              className="cursor-pointer"
              onClick={handleCloseDiscussion}
            />
          </div>
          <div className="flex-1 flex bg-white px-3 py-2 round-xl m-4">
            <iframe
              src={createWebChatUrl(activeDiscussion)}
              title="Webchat"
              className="flex-1"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default withNOVOHeader(Discussions);
